import React from "react";
import { useDrop } from "react-dnd";
import DeleteIcon from "@mui/icons-material/Delete";

// Pixels per millimetre for the Snap-On absolute-position renderer.
// The whole panel is scaled by CSS transform; this only fixes the internal ratio.
const PX_PER_MM = 3.78;
const BTN_MM = 12; // switch / fan circle diameter
const mm = (v) => `${v * PX_PER_MM}px`;

// A single droppable Snap-On button (switch / fan on-off / fan speed).
// Positioned absolutely by its centre (cx, cy) in mm from the panel top-left.
const SnapOnButton = ({ el, idx, sku, activeStep, onDrop }) => {
  const id = `snapon-${sku}-${idx}`;
  const [, drop] = useDrop(
    () => ({
      accept: "icon",
      drop: (item) => {
        const host = document.getElementById(`${id}-svg`);
        const wrap = document.getElementById(id);
        if (wrap) {
          wrap.style.border = "none";
          wrap.style.backgroundImage = "none";
        }
        if (host) {
          fetch(item.icon)
            .then((r) => r.text())
            .then((svg) => {
              host.innerHTML = svg;
            })
            .catch(() => {});
        }
        if (onDrop) onDrop(sku, idx, item.icon, el);
      },
    }),
    [id]
  );

  return (
    <div
      ref={drop}
      id={id}
      className="snapon-btn"
      style={{
        position: "absolute",
        left: mm(el.cx - BTN_MM / 2),
        top: mm(el.cy - BTN_MM / 2),
        width: mm(BTN_MM),
        height: mm(BTN_MM),
      }}
    >
      {activeStep === 3 && (
        <DeleteIcon
          className="delete"
          onClick={() => {
            const wrap = document.getElementById(id);
            const host = document.getElementById(`${id}-svg`);
            if (wrap) {
              wrap.style.backgroundImage =
                "url(./assets/Shapes/VERTICALLINE.svg)";
            }
            if (host) host.innerHTML = "";
          }}
          fontSize="small"
          style={{
            position: "absolute",
            top: "-16px",
            right: "-16px",
            color: "white",
            cursor: "pointer",
          }}
        />
      )}
      <div id={`${id}-svg`} className="snapon-btn-svg" />
    </div>
  );
};

// A static (non-interactive) circle: socket big/small circle, on/off placeholder, etc.
const StaticCircle = ({ el, sizeMm = 12, cls = "" }) => (
  <div
    className={`snapon-static-circle ${cls}`}
    style={{
      position: "absolute",
      left: mm(el.cx - sizeMm / 2),
      top: mm(el.cy - sizeMm / 2),
      width: mm(sizeMm),
      height: mm(sizeMm),
    }}
  />
);

// A socket earth pin (D-shaped-ish; rendered as a rounded static mark).
const EarthPin = ({ el }) => (
  <div
    className="snapon-earth-pin"
    style={{
      position: "absolute",
      left: mm(el.cx - 3.5),
      top: mm(el.cy - 3),
      width: mm(7),
      height: mm(6),
    }}
  />
);

// Renders one Snap-On SKU inside the #panel container. Buttons are droppable;
// socket parts are static graphics. Panel is sized in mm (× PX_PER_MM).
const SnapOnPanel = ({ sku, activeStep, onDrop }) => {
  if (!sku) return null;
  const { panel, elements } = sku;

  return (
    <div
      className="snapon-surface"
      style={{
        position: "relative",
        width: mm(panel.w),
        height: mm(panel.h),
      }}
    >
      {elements.map((el, idx) => {
        if (el.drop) {
          return (
            <SnapOnButton
              key={idx}
              el={el}
              idx={idx}
              sku={sku.sku}
              activeStep={activeStep}
              onDrop={onDrop}
            />
          );
        }
        // static socket parts
        if (el.type === "socket-big-circle")
          return <StaticCircle key={idx} el={el} sizeMm={13} cls="socket-big" />;
        if (el.type === "socket-small-circle")
          return <StaticCircle key={idx} el={el} sizeMm={7} cls="socket-small" />;
        if (el.type === "socket-earth-pin")
          return <EarthPin key={idx} el={el} />;
        return <StaticCircle key={idx} el={el} sizeMm={10} />;
      })}
    </div>
  );
};

export default SnapOnPanel;
export { PX_PER_MM, BTN_MM };
